/**
 * useConsolidatedPortfolio Hook
 * Hook for managing consolidated portfolio data
 * 
 * ⚠️ CRITICAL: Per ADR-FE-001, this hook only fetches and displays pre-calculated data.
 * All portfolio calculations and aggregations are performed by backend Portfolio Service.
 */

import { useEffect, useCallback, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  fetchConsolidatedPortfolio,
  fetchHoldings,
  syncPortfolio,
} from '../../../store/slices/portfolioSlice';
import {
  selectConsolidatedPortfolio,
  selectPortfolioSummary,
  selectTopHoldings,
  selectAllHoldings,
  selectBrokerAccountSummaries,
  selectPortfolioLoading,
  selectPortfolioError,
  selectPortfolioLastUpdated,
  selectSyncInProgress,
} from '../../../store/selectors/portfolioSelectors';
import { CACHE_TTL } from '../../../utils/constants';

export interface UseConsolidatedPortfolioOptions {
  /**
   * Auto-refresh interval in milliseconds
   * Set to 0 to disable auto-refresh
   */
  autoRefreshInterval?: number;
  /**
   * Fetch detailed holdings list
   */
  includeHoldings?: boolean;
  /**
   * Broker account ID to filter holdings
   */
  brokerAccountId?: string;
}

export interface UseConsolidatedPortfolioResult {
  /**
   * Consolidated portfolio data (pre-calculated by backend)
   */
  portfolio: ReturnType<typeof selectConsolidatedPortfolio>;
  /**
   * Portfolio summary (pre-calculated by backend)
   */
  summary: ReturnType<typeof selectPortfolioSummary>;
  /**
   * Top holdings (pre-aggregated by backend)
   */
  topHoldings: ReturnType<typeof selectTopHoldings>;
  /**
   * All holdings (detailed list)
   */
  holdings: ReturnType<typeof selectAllHoldings>;
  /**
   * Broker account summaries (pre-calculated by backend)
   */
  brokerAccounts: ReturnType<typeof selectBrokerAccountSummaries>;
  /**
   * Loading state
   */
  loading: boolean;
  /**
   * Error message
   */
  error: string | null;
  /**
   * Last updated timestamp
   */
  lastUpdated: number | null;
  /**
   * Sync in progress status
   */
  syncInProgress: Record<string, boolean>;
  /**
   * Refresh portfolio data
   */
  refresh: () => Promise<void>;
  /**
   * Refresh holdings list
   */
  refreshHoldings: (brokerAccountId?: string) => Promise<void>;
  /**
   * Trigger portfolio sync
   */
  triggerSync: (brokerAccountId?: string, scope?: 'holdings_only' | 'transactions_only' | 'both') => Promise<void>;
  /**
   * Check if data is stale (needs refresh)
   */
  isStale: boolean;
}

export function useConsolidatedPortfolio(
  options: UseConsolidatedPortfolioOptions = {}
): UseConsolidatedPortfolioResult {
  const dispatch = useAppDispatch();
  const {
    autoRefreshInterval = CACHE_TTL.PORTFOLIO,
    includeHoldings = false,
    brokerAccountId,
  } = options;

  // Selectors
  const portfolio = useAppSelector(selectConsolidatedPortfolio);
  const summary = useAppSelector(selectPortfolioSummary);
  const topHoldings = useAppSelector(selectTopHoldings);
  const holdings = useAppSelector(selectAllHoldings);
  const brokerAccounts = useAppSelector(selectBrokerAccountSummaries);
  const loading = useAppSelector(selectPortfolioLoading);
  const error = useAppSelector(selectPortfolioError);
  const lastUpdated = useAppSelector(selectPortfolioLastUpdated);
  const syncInProgress = useAppSelector(selectSyncInProgress);

  // Track if a refresh is in progress to prevent race conditions
  const refreshInProgressRef = useRef(false);
  // Track if we have already attempted the initial holdings load when includeHoldings is true.
  // Prevents infinite refetch loop when backend returns empty holdings (holdings.length === 0 && !loading would re-trigger otherwise).
  const holdingsLoadAttemptedRef = useRef(false);

  /**
   * Check if data is stale
   */
  const isStale = useMemo(() => {
    if (!lastUpdated) return true;
    return Date.now() - lastUpdated > CACHE_TTL.PORTFOLIO;
  }, [lastUpdated]);

  /**
   * Refresh consolidated portfolio
   * Fetches ALL portfolio data from database, regardless of broker connection status.
   * Shows all portfolio information that exists in the database.
   * Prevents multiple simultaneous refresh calls to avoid race conditions.
   */
  const refresh = useCallback(async () => {
    // Prevent multiple simultaneous refresh calls
    if (refreshInProgressRef.current) {
      return;
    }
    
    refreshInProgressRef.current = true;
    try {
      await dispatch(fetchConsolidatedPortfolio()).unwrap();
    } catch (err) {
      // Error handled by Redux slice
      // eslint-disable-next-line no-console
      console.error('Failed to refresh portfolio:', err);
    } finally {
      refreshInProgressRef.current = false;
    }
  }, [dispatch]);

  /**
   * Refresh holdings list
   */
  const refreshHoldings = useCallback(
    async (accountId?: string) => {
      try {
        await dispatch(
          fetchHoldings({
            brokerAccountId: accountId || brokerAccountId,
          })
        ).unwrap();
      } catch (err) {
        // Error handled by Redux slice
        // eslint-disable-next-line no-console
        console.error('Failed to refresh holdings:', err);
      }
    },
    [dispatch, brokerAccountId]
  );

  /**
   * Trigger portfolio sync
   */
  const triggerSync = useCallback(
    async (
      accountId?: string,
      scope: 'holdings_only' | 'transactions_only' | 'both' = 'both'
    ) => {
      try {
        await dispatch(
          syncPortfolio({
            brokerAccountId: accountId || brokerAccountId,
            scope,
          })
        ).unwrap();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to sync portfolio:', err);
      }
      
      try {
        await new Promise(resolve => setTimeout(resolve, 5000));
        await refresh();
        await new Promise(resolve => setTimeout(resolve, 3000));
        await refresh();
        
        if (includeHoldings) {
          await refreshHoldings(accountId || brokerAccountId);
        }
      } catch (refreshErr) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh portfolio after sync:', refreshErr);
      }
    },
    [dispatch, brokerAccountId, includeHoldings, refresh, refreshHoldings]
  );

  /**
   * Initial load
   * Always fetch on mount to ensure data from DB is loaded
   * This ensures that when user logs in, existing portfolio data is fetched from database
   */
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  /**
   * Load holdings if requested (initial load only).
   * Only run once when includeHoldings becomes true. Do not re-run when holdings.length === 0,
   * otherwise when the backend has no data we get an infinite loop: empty response → loading false
   * → effect sees holdings.length === 0 && !loading → refreshHoldings() again.
   */
  useEffect(() => {
    if (includeHoldings && !holdingsLoadAttemptedRef.current) {
      holdingsLoadAttemptedRef.current = true;
      refreshHoldings();
    }
  }, [includeHoldings, refreshHoldings]);

  /**
   * Auto-refresh interval
   * Only refresh if data is stale or if no refresh is in progress
   */
  useEffect(() => {
    if (autoRefreshInterval > 0) {
      const interval = setInterval(() => {
        // Only auto-refresh if data is stale or if no manual refresh is in progress
        if (isStale && !refreshInProgressRef.current) {
          refresh();
          if (includeHoldings) {
            refreshHoldings();
          }
        }
      }, autoRefreshInterval);

      return () => clearInterval(interval);
    }
  }, [autoRefreshInterval, includeHoldings, refresh, refreshHoldings, isStale]);

  return {
    portfolio,
    summary,
    topHoldings,
    holdings,
    brokerAccounts,
    loading,
    error,
    lastUpdated,
    syncInProgress,
    refresh,
    refreshHoldings,
    triggerSync,
    isStale,
  };
}

